import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { customersAPI, adminAPI, usersAPI, quotesAPI, contractsAPI } from '../services/api';
import { ContactsTab } from '../components/ContactsTab';
import { AddressFields } from '../components/AddressFields';
import { useAuth } from '../auth/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CountrySelect } from '../components/CountrySelect';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  ArrowLeft, Plus, Trash2, Star, Loader2, Phone, Mail,
  PowerOff, Power, AlertCircle, Save, CheckCircle2,
  FileText, Smartphone, Globe, XCircle, FileSignature,
  Clock, AlertTriangle, Shield, Upload, Download, FileDown,
  ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';

const NAVY   = 'hsl(var(--c4p-navy-900))';
const ORANGE = 'hsl(var(--c4p-orange-600))';

const CURRENCIES    = ['GBP', 'EUR', 'USD', 'CAD', 'AUD'];
const PARTNER_TYPES = ['SAP Partner', 'VAR', 'ISV', 'MSP', 'System Integrator', 'Reseller', 'Distributor', 'Other'];
const CURRENCY_SYMBOLS = { GBP: '\u00a3', EUR: '\u20ac', USD: '$', CAD: 'CA$', AUD: 'A$' };
const STATUS_STYLES = {
  DRAFT:    { bg: '#f8fafc', color: '#475569', border: '#e2e8f0' },
  SENT:     { bg: '#f0f9ff', color: '#0c4a6e', border: '#bae6fd' },
  ACCEPTED: { bg: '#f0fdf4', color: '#14532d', border: '#bbf7d0' },
  EXPIRED:  { bg: '#fffbeb', color: '#78350f', border: '#fde68a' },
};

const defaultContact = { name: '', title: '', email: '', phone: '', is_primary: false, notes: '' };
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '\u2014';
const fmtMoney = (n, code) => `${CURRENCY_SYMBOLS[code] || ''}${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.DRAFT;
  return <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>{status}</span>;
};

const PRODUCT_DISPLAY = { STANDARD: 'Standard', ADVANCED: 'Advanced', MTPRO: 'MTPro' };
const ProductBadge = ({ code }) => (
  <span className="text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap"
    style={code === 'STANDARD'
      ? { background: 'hsl(var(--muted))', color: 'hsl(var(--fg))' }
      : { background: NAVY, color: 'white' }}>
    {PRODUCT_DISPLAY[code] || code}
  </span>
);

const CategoryBadge = ({ category }) => {
  if (!category || category === 'PRODUCTION') return null;
  const styles = {
    TEST_SYSTEM: { background: '#fff7ed', color: '#ea580c', border: '1px solid #fdba74' },
    BESPOKE:     { background: '#f5f3ff', color: '#7c3aed', border: '1px solid #c4b5fd' },
  };
  const labels = { TEST_SYSTEM: 'Test System', BESPOKE: 'Custom' };
  return (
    <span className="text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap"
      style={styles[category] || {}}>
      {labels[category] || category}
    </span>
  );
};

const ExpiryCell = ({ expiryDate }) => {
  if (!expiryDate) return <span style={{ color: 'hsl(var(--muted-fg))' }}>-</span>;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const exp = new Date(expiryDate); exp.setHours(0, 0, 0, 0);
  const diffDays = Math.round((exp - today) / 86400000);
  const fmtd = exp.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  if (diffDays < 0) return <span className="flex items-center gap-1 text-xs font-medium" style={{ color: '#b91c1c' }}><AlertTriangle size={11} />{fmtd}</span>;
  if (diffDays <= 7) return <span className="flex items-center gap-1 text-xs font-medium" style={{ color: '#d97706' }}><Clock size={11} />{fmtd}</span>;
  return <span className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>{fmtd}</span>;
};

const billingLabel = (model) => model === 'TENANT_DIRECT' ? 'Tenant Direct' : model === 'DIRECT' ? 'Direct' : 'Partner';

// ── Profile Tab ─────────────────────────────────────────────────────────────
function GdprExportButton({ entityId, entityType, entityName }) {
  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      const { data, headers } = await adminAPI.exportCustomerData(entityId);
      const blob = new Blob([data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gdpr_export_${entityName.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('GDPR data export downloaded');
    } catch { toast.error('Failed to generate GDPR export'); }
    finally { setExporting(false); }
  };
  return (
    <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting}
      title="Download all personal data held for this entity (GDPR SAR)"
      data-testid="gdpr-export-btn"
      style={{ borderColor: '#d1d5db', color: '#64748b' }}>
      {exporting ? <Loader2 size={12} className="animate-spin mr-1" /> : <ShieldCheck size={12} className="mr-1" />}
      GDPR Export
    </Button>
  );
}

function ProfileTab({ customer, regions, psmUsers, paymentMethods = ['Direct Debit', 'Wise', 'Bank Transfer', 'Credit Card', 'BACS'], onSaved }) {
  const [form, setForm] = useState({ ...customer });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const [err, setErr]       = useState(null);
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const [showMsaForm, setShowMsaForm] = useState(false);
  const [msaVersion, setMsaVersion]   = useState('');
  const [msaDate, setMsaDate]         = useState('');
  const [savingMsa, setSavingMsa]     = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [downloadingDoc, setDownloadingDoc] = useState(false);

  // Sync if parent reloads
  useEffect(() => { setForm({ ...customer }); }, [customer]);

  const handleRegionChange = (regionId) => {
    const r = regions.find(r => r.id === regionId);
    setForm(f => ({
      ...f,
      default_region_id:   regionId === 'none' ? null : regionId,
      default_region_name: r?.name || null,
      default_currency:    r?.default_currency || f.default_currency,
    }));
  };

  const handlePsmChange = (userId) => {
    const u = psmUsers.find(u => u.id === userId);
    setForm(f => ({
      ...f,
      default_psm_id:   userId === 'none' ? null : userId,
      default_psm_name: u?.name || null,
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.company_name?.trim()) return toast.error('Company name is required');
    setSaving(true); setErr(null); setSaved(false);
    try {
      await customersAPI.update(customer.id, {
        company_name:        form.company_name,
        partner_type:        form.partner_type,
        website:             form.website || null,
        billing_address:     form.billing_address || null,
        addr_line1:          form.addr_line1 || null,
        addr_line2:          form.addr_line2 || null,
        addr_city:           form.addr_city || null,
        addr_region:         form.addr_region || null,
        addr_postcode:       form.addr_postcode || null,
        country:             form.country || null,
        default_currency:    form.default_currency,
        default_region_id:   form.default_region_id || null,
        default_region_name: form.default_region_name || null,
        default_psm_id:           form.default_psm_id || null,
        default_psm_name:         form.default_psm_name || null,
        default_billing_frequency: form.default_billing_frequency || null,
        default_payment_method:    form.default_payment_method || null,
        notes:               form.notes || null,
      });
      toast.success('Partner profile saved');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      onSaved();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleMsaSave = async (e) => {
    e.preventDefault();
    setSavingMsa(true);
    try {
      await customersAPI.updateMsa(customer.id, {
        msa_version: msaVersion.trim() || null,
        msa_signed_date: msaDate || null,
      });
      toast.success('MSA recorded as signed');
      setShowMsaForm(false);
      onSaved();
    } catch (ex) {
      toast.error(ex.response?.data?.detail || 'Failed to record MSA');
    } finally { setSavingMsa(false); }
  };

  const handleMsaDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await customersAPI.uploadMsaDoc(customer.id, fd);
      toast.success('MSA document uploaded successfully');
      onSaved();
    } catch (ex) {
      toast.error(ex.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingDoc(false);
      e.target.value = '';
    }
  };

  const handleMsaDocDownload = async () => {
    setDownloadingDoc(true);
    try {
      const { data, headers } = await customersAPI.getMsaDoc(customer.id);
      const contentType = headers['content-type'] || 'application/octet-stream';
      const blob = new Blob([data], { type: contentType });
      const url = URL.createObjectURL(blob);
      if (contentType === 'application/pdf') {
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = customer.msa_document?.filename || 'msa_document';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (ex) {
      toast.error('Failed to download MSA document');
    } finally { setDownloadingDoc(false); }
  };

  return (
    <div className="space-y-6 pt-2">
      {err && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{err}</AlertDescription></Alert>}

      {/* ── MSA Status Section ── */}
      <div className="rounded-xl border p-4 space-y-3" data-testid="partner-msa-section"
        style={{ background: customer.msa_signed ? '#f0fdf4' : '#fef2f2', borderColor: customer.msa_signed ? '#bbf7d0' : '#fecaca' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield size={14} style={{ color: customer.msa_signed ? '#16a34a' : '#dc2626' }} />
            <span className="text-sm font-semibold" style={{ color: customer.msa_signed ? '#14532d' : '#991b1b' }}>
              Master Service Agreement
            </span>
          </div>
          {customer.msa_signed ? (
            <span className="text-xs font-medium px-2 py-0.5 rounded-full"
              style={{ background: '#dcfce7', color: '#14532d', border: '1px solid #bbf7d0' }}
              data-testid="msa-signed-badge">
              Signed
            </span>
          ) : (
            <span className="text-xs font-medium px-2 py-0.5 rounded-full"
              style={{ background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }}
              data-testid="msa-unsigned-badge">
              Not Signed
            </span>
          )}
        </div>
        {customer.msa_signed && (
          <div className="text-xs space-y-0.5" style={{ color: '#166534' }}>
            {customer.msa_version && <div>Version: <span className="font-medium">{customer.msa_version}</span></div>}
            {customer.msa_signed_date && <div>Date: <span className="font-medium">{customer.msa_signed_date}</span></div>}
            {customer.msa_signed_by_name && <div>Recorded by: <span className="font-medium">{customer.msa_signed_by_name}</span></div>}
          </div>
        )}
        {!customer.msa_signed && (
          <p className="text-xs" style={{ color: '#991b1b' }}>
            Quotes cannot be sent for signing until the MSA is recorded as signed by a Super Admin.
          </p>
        )}

        {/* ── MSA Document (upload / download) ── */}
        <div className="pt-1">
          {customer.msa_document ? (
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg flex-1 min-w-0"
                style={{ background: 'white', border: '1px solid #d1fae5', color: '#065f46' }}>
                <FileDown size={12} className="flex-shrink-0" />
                <span className="truncate font-medium">{customer.msa_document.filename}</span>
                <span className="flex-shrink-0" style={{ color: '#6ee7b7' }}>
                  &middot; {customer.msa_document.uploaded_at ? new Date(customer.msa_document.uploaded_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
                </span>
              </div>
              <Button size="sm" type="button" variant="outline" onClick={handleMsaDocDownload}
                disabled={downloadingDoc}
                style={{ borderColor: '#6ee7b7', color: '#065f46', flexShrink: 0 }}
                data-testid="msa-doc-download-btn">
                {downloadingDoc ? <Loader2 size={12} className="animate-spin mr-1" /> : <Download size={12} className="mr-1" />}
                View / Download
              </Button>
              {isSuperAdmin && (
                <label className="cursor-pointer flex-shrink-0">
                  <input type="file" accept=".pdf,.doc,.docx" className="hidden"
                    onChange={handleMsaDocUpload} disabled={uploadingDoc}
                    data-testid="msa-doc-replace-input" />
                  <Button size="sm" type="button" variant="outline" asChild disabled={uploadingDoc}
                    style={{ borderColor: '#d1d5db', color: '#6b7280', pointerEvents: uploadingDoc ? 'none' : 'auto' }}>
                    <span>
                      {uploadingDoc ? <Loader2 size={12} className="animate-spin mr-1" /> : <Upload size={12} className="mr-1" />}
                      Replace
                    </span>
                  </Button>
                </label>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <p className="text-xs flex-1" style={{ color: customer.msa_signed ? '#166534' : '#991b1b', opacity: 0.8 }}>
                No document uploaded yet.
              </p>
              {isSuperAdmin && (
                <label className="cursor-pointer flex-shrink-0">
                  <input type="file" accept=".pdf,.doc,.docx" className="hidden"
                    onChange={handleMsaDocUpload} disabled={uploadingDoc}
                    data-testid="msa-doc-upload-input" />
                  <Button size="sm" type="button" variant="outline" asChild disabled={uploadingDoc}
                    style={{ borderColor: customer.msa_signed ? '#86efac' : '#fca5a5', color: customer.msa_signed ? '#15803d' : '#b91c1c', pointerEvents: uploadingDoc ? 'none' : 'auto' }}>
                    <span>
                      {uploadingDoc ? <Loader2 size={12} className="animate-spin mr-1" /> : <Upload size={12} className="mr-1" />}
                      Upload Signed MSA
                    </span>
                  </Button>
                </label>
              )}
            </div>
          )}
        </div>
        {isSuperAdmin && !showMsaForm && (
          <Button size="sm" type="button" variant="outline" onClick={() => setShowMsaForm(true)}
            style={{ borderColor: customer.msa_signed ? '#86efac' : '#fca5a5', color: customer.msa_signed ? '#15803d' : '#b91c1c' }}
            data-testid="msa-record-btn">
            <Shield size={12} className="mr-1.5" />
            {customer.msa_signed ? 'Update MSA Record' : 'Record MSA as Signed'}
          </Button>
        )}
        {isSuperAdmin && showMsaForm && (
          <form onSubmit={handleMsaSave} className="space-y-3 pt-2 border-t"
            style={{ borderColor: customer.msa_signed ? '#bbf7d0' : '#fecaca' }}>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <label className="text-xs font-medium" style={{ color: '#475569' }}>Version / Reference</label>
                <Input value={msaVersion} onChange={e => setMsaVersion(e.target.value)}
                  placeholder="e.g. Partner Agreement v1.7" className="h-8 text-xs"
                  data-testid="msa-version-input" />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium" style={{ color: '#475569' }}>Date Signed</label>
                <Input type="date" value={msaDate} onChange={e => setMsaDate(e.target.value)}
                  className="h-8 text-xs" data-testid="msa-date-input" />
              </div>
            </div>
            <div className="flex gap-2">
              <Button type="submit" size="sm" disabled={savingMsa}
                style={{ background: '#16a34a', color: 'white' }}
                data-testid="msa-confirm-btn">
                {savingMsa ? <Loader2 size={11} className="animate-spin mr-1" /> : null}
                Confirm
              </Button>
              <Button type="button" size="sm" variant="outline"
                onClick={() => { setShowMsaForm(false); setMsaVersion(''); setMsaDate(''); }}>
                Cancel
              </Button>
            </div>
          </form>
        )}
      </div>

      <form onSubmit={handleSave} className="space-y-6">

        {/* ─ Core Details ─ */}
        <div className="rounded-xl border bg-white p-5 space-y-4">
          <h3 className="text-sm font-bold" style={{ color: NAVY }}>Company Details</h3>

          <div className="space-y-1.5">
            <Label>Company Name *</Label>
            <Input value={form.company_name || ''} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} required data-testid="edit-company-name" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Partner Type</Label>
              <Select value={form.partner_type || 'SAP Partner'} onValueChange={v => setForm(f => ({ ...f, partner_type: v }))}>
                <SelectTrigger data-testid="edit-partner-type"><SelectValue /></SelectTrigger>
                <SelectContent>{PARTNER_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Website</Label>
              <Input value={form.website || ''} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} placeholder="https://" data-testid="edit-website" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Country</Label>
              <CountrySelect value={form.country || ''} onChange={v => setForm(f => ({ ...f, country: v }))} testId="edit-country" />
            </div>
            <div className="space-y-1.5">
              <Label>Default Currency</Label>
              <Select value={form.default_currency || 'GBP'} onValueChange={v => setForm(f => ({ ...f, default_currency: v }))}>
                <SelectTrigger data-testid="edit-currency"><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {/* Structured Address */}
          <AddressFields form={form} setForm={setForm} />

          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea value={form.notes || ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="h-20 resize-none" placeholder="Internal notes about this partner..." data-testid="edit-notes" />
          </div>
        </div>

        {/* ─ Defaults ─ */}
        <div className="rounded-xl border bg-white p-5 space-y-4">
          <h3 className="text-sm font-bold" style={{ color: NAVY }}>Defaults for New Quotes</h3>

          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5"><Globe size={13} style={{ color: 'hsl(var(--muted-fg))' }} /> Default AWS Region</Label>
            <Select value={form.default_region_id || 'none'} onValueChange={handleRegionChange}>
              <SelectTrigger data-testid="edit-default-region"><SelectValue placeholder="No default region" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No default region</SelectItem>
                {regions.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Auto-sets region and currency when this partner is selected in a new quote.</p>
          </div>

          <div className="space-y-1.5">
            <Label>Default Partner Success Manager (PSM)</Label>
            <Select value={form.default_psm_id || 'none'} onValueChange={handlePsmChange}>
              <SelectTrigger data-testid="edit-default-psm"><SelectValue placeholder="No default PSM" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No default PSM</SelectItem>
                {psmUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Auto-assigned as PSM when a new quote is created for this partner.</p>
          </div>

          <div className="space-y-1.5">
            <Label>Default billing frequency</Label>
            <Select value={form.default_billing_frequency || 'none'} onValueChange={v => setForm(f => ({ ...f, default_billing_frequency: v === 'none' ? null : v }))}>
              <SelectTrigger data-testid="edit-default-billing-frequency"><SelectValue placeholder="No default" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No default</SelectItem>
                <SelectItem value="Monthly">Monthly</SelectItem>
                <SelectItem value="Quarterly">Quarterly</SelectItem>
                <SelectItem value="Annually">Annually</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Pre-populates the billing frequency on new quotes for this partner.</p>
          </div>

          <div className="space-y-1.5">
            <Label>Default payment method</Label>
            <Select value={form.default_payment_method || 'none'} onValueChange={v => setForm(f => ({ ...f, default_payment_method: v === 'none' ? null : v }))}>
              <SelectTrigger data-testid="edit-default-payment-method"><SelectValue placeholder="No default" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No default</SelectItem>
                {paymentMethods.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Pre-populates the payment method on new quotes for this partner.</p>
          </div>
        </div>

        {/* Save */}
        <div className="flex items-center gap-3">
          <Button type="submit" disabled={saving} style={{ background: NAVY, color: 'white' }} className="gap-2" data-testid="save-partner-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={14} />}
            Save Changes
          </Button>
          {saved && (
            <span className="flex items-center gap-1 text-sm" style={{ color: '#15803d' }}>
              <CheckCircle2 size={14} /> Saved
            </span>
          )}
        </div>
      </form>
    </div>
  );
}

// ── Contacts Tab ─────────────────────────────────────────────────────────────
// CustomerContactsTab wraps the shared ContactsTab with partner-specific API calls

function CustomerContactsTab({ customer, onReload }) {
  return (
    <ContactsTab
      entityId={customer.id} entityName={customer.company_name}
      contacts={customer.contacts || []}
      onAdd={data => customersAPI.addContact(customer.id, data).then(r => r.data)}
      onUpdate={(cid, data) => customersAPI.updateContact(customer.id, cid, data).then(r => r.data)}
      onDelete={cid => customersAPI.deleteContact(customer.id, cid)}
      noun="partner"
    />
  );
}


// ── Quotes Tab ─────────────────────────────────────────────────────────────────
function QuotesTab({ customerId, companyName }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  const [quotes, setQuotes]         = useState([]);
  const [total, setTotal]           = useState(0);
  const [loading, setLoading]       = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting]     = useState(false);

  const load = useCallback(() => {
    if (!customerId) return;
    setLoading(true);
    quotesAPI.list({ customer_id: customerId, page_size: 100 })
      .then(r => { setQuotes(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => setQuotes([]))
      .finally(() => setLoading(false));
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await quotesAPI.delete(deleteTarget.id);
      toast.success(`${deleteTarget.quote_number} deleted`);
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
    finally { setDeleting(false); }
  };

  const canDelete = (q) => q.status !== 'ACCEPTED' || isSuperAdmin;

  if (loading) return <div className="space-y-3 pt-2">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>;

  return (
    <div className="space-y-4 pt-2">
      <div className="flex items-center justify-between">
        <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
          {total === 0 ? `No quotes for ${companyName} yet.` : `${total} quote(s) for ${companyName}`}
        </p>
        <Link to="/quotes/new">
          <Button size="sm" style={{ background: ORANGE, color: 'white' }} className="gap-1">
            <Plus size={13} /> New Quote
          </Button>
        </Link>
      </div>

      {quotes.length === 0 ? (
        <div className="rounded-xl border bg-white p-10 text-center">
          <FileText size={32} className="mx-auto mb-3" style={{ color: 'hsl(var(--border))' }} />
          <p className="text-sm font-medium" style={{ color: NAVY }}>No quotes yet</p>
          <p className="text-xs mt-1" style={{ color: 'hsl(var(--muted-fg))' }}>Create a quote for this partner to see it here.</p>
        </div>
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow style={{ background: 'hsl(var(--muted))' }}>
                  <TableHead>Quote #</TableHead>
                  <TableHead>Billing</TableHead>
                  <TableHead>Tenant</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Users</TableHead>
                  <TableHead>Monthly</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Expiry</TableHead>
                  <TableHead>PSM</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {quotes.map(q => (
                  <TableRow key={q.id} onClick={() => navigate(`/quotes/${q.id}`)}
                    className="data-table-row cursor-pointer transition-colors hover:bg-slate-50">
                    <TableCell>
                      <span className="font-mono text-sm font-semibold" style={{ color: NAVY }}>{q.quote_number}</span>
                      <span className="text-xs ml-1" style={{ color: 'hsl(var(--muted-fg))' }}>v{q.version}</span>
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap" style={{ color: 'hsl(var(--muted-fg))' }}>
                      {billingLabel(q.billing_model)}
                    </TableCell>
                    <TableCell className="text-sm max-w-[130px] truncate" style={{ color: 'hsl(var(--muted-fg))' }}>
                      {q.tenant_name || '\u2014'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 flex-wrap">
                        {(!q.quote_category || q.quote_category === 'PRODUCTION') && <ProductBadge code={q.product_code} />}
                        <CategoryBadge category={q.quote_category} />
                      </div>
                    </TableCell>
                    <TableCell className="text-sm tabular-nums">{q.num_users}</TableCell>
                    <TableCell className="text-sm font-semibold tabular-nums">
                      {q.pricing_snapshot
                        ? fmtMoney(
                            q.billing_model === 'TENANT_DIRECT' && q.pricing_snapshot.tenant_invoice_monthly
                              ? q.pricing_snapshot.tenant_invoice_monthly
                              : q.pricing_snapshot.monthly_total ?? 0,
                            q.currency
                          ) + '/mo'
                        : '\u2014'}
                    </TableCell>
                    <TableCell><StatusBadge status={q.status} /></TableCell>
                    <TableCell><ExpiryCell expiryDate={q.expiry_date} /></TableCell>
                    <TableCell className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
                      {q.psm_name || q.psm_user_name || '\u2014'}
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap" style={{ color: 'hsl(var(--muted-fg))' }}>
                      {fmtDate(q.created_at)}
                    </TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      {canDelete(q) && (
                        <Button variant="ghost" size="sm"
                          onClick={e => { e.stopPropagation(); setDeleteTarget({ id: q.id, quote_number: q.quote_number }); }}
                          style={{ color: 'hsl(var(--danger))' }}
                          title={q.status === 'ACCEPTED' ? 'Delete signed quote (Super Admin only)' : 'Delete quote'}
                          data-testid={`partner-delete-quote-${q.id}`}>
                          <Trash2 size={13} />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 size={16} style={{ color: 'hsl(var(--danger))' }} />
              Delete Quote
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
            Are you sure you want to delete <strong>{deleteTarget?.quote_number}</strong>?
            This action cannot be undone.
          </p>
          {deleteTarget && quotes.find(q => q.id === deleteTarget.id)?.status === 'ACCEPTED' && (
            <div className="rounded-lg px-3 py-2 text-xs flex items-start gap-2"
              style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#7f1d1d' }}>
              <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />
              <span>This quote has been digitally signed. Deleting it will remove the audit record. Only proceed if you are certain this is required.</span>
            </div>
          )}
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button onClick={handleDelete} disabled={deleting}
              style={{ background: 'hsl(var(--danger))', color: 'white' }}
              data-testid="confirm-delete-partner-quote">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete Quote'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Contracts Tab ────────────────────────────────────────────────────────────
function ContractsTab({ customerId, companyName }) {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!customerId) return;
    setLoading(true);
    contractsAPI.list({ customer_id: customerId, status: 'ALL', limit: 100 })
      .then(r => setContracts(r.data.contracts || []))
      .catch(() => setContracts([]))
      .finally(() => setLoading(false));
  }, [customerId]);

  if (loading) return <div className="space-y-3 pt-2">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>;

  return (
    <div className="space-y-4 pt-2">
      <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
        {contracts.length === 0 ? `No contracts for ${companyName} yet.` : `${contracts.length} contract(s) for ${companyName}`}
      </p>
      {contracts.length === 0 ? (
        <div className="rounded-xl border bg-white p-10 text-center">
          <FileSignature size={32} className="mx-auto mb-3" style={{ color: 'hsl(var(--border))' }} />
          <p className="text-sm font-medium" style={{ color: NAVY }}>No contracts yet</p>
          <p className="text-xs mt-1" style={{ color: 'hsl(var(--muted-fg))' }}>Contracts are created automatically when a quote is signed.</p>
        </div>
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow style={{ background: 'hsl(var(--muted))' }}>
                <TableHead>Contract #</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Product</TableHead>
                <TableHead>Pricing</TableHead>
                <TableHead>Users</TableHead>
                <TableHead>Monthly</TableHead>
                <TableHead>PSM</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contracts.map(c => (
                <TableRow key={c.id} onClick={() => navigate(`/contracts/${c.id}`)}
                  className="data-table-row cursor-pointer transition-colors hover:bg-slate-50">
                  <TableCell>
                    <span className="font-mono text-sm font-semibold" style={{ color: NAVY }}>{c.contract_number}</span>
                  </TableCell>
                  <TableCell className="text-sm max-w-[180px] truncate">{c.contract_name || '\u2014'}</TableCell>
                  <TableCell>
                    {(() => {
                      const cat = c.quote_category;
                      const pc = c.product_code;
                      const isCustom = cat && cat !== 'PRODUCTION';
                      if (isCustom) {
                        return (
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full"
                            style={{ background: '#f5f3ff', color: '#7c3aed', border: '1px solid #c4b5fd' }}>
                            {cat === 'TEST_SYSTEM' ? 'Custom Test' : 'Custom'}
                          </span>
                        );
                      }
                      return (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full"
                          style={pc !== 'STANDARD' ? { background: NAVY, color: 'white' } : { background: 'hsl(var(--muted))', color: 'hsl(var(--fg))' }}>
                          {pc}
                        </span>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {c.contract_source === 'manual' && (
                      <span className="text-xs font-medium px-1.5 py-0.5 rounded"
                        style={{ background: '#fff7ed', color: '#9a3412', border: '1px solid #fdba74' }}>
                        Manual Pricing
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm tabular-nums">{c.num_users ?? '\u2014'}</TableCell>
                  <TableCell className="text-sm font-semibold tabular-nums">
                    {c.monthly_total != null ? fmtMoney(c.monthly_total, c.currency) + '/mo' : '\u2014'}
                  </TableCell>
                  <TableCell className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>{c.psm_user_name || '\u2014'}</TableCell>
                  <TableCell>
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full"
                      style={c.status === 'LIVE'
                        ? { background: '#f0fdf4', color: '#14532d', border: '1px solid #bbf7d0' }
                        : { background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' }}>
                      {c.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>{fmtDate(c.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

// ── Main Page ───────────────────────────────────────────────────────────────────
export default function CustomerDetailPage() {
  const { id } = useParams();
  const { isAdmin, isManager, user: authUser } = useAuth();
  const navigate = useNavigate();

  const [customer, setCustomer] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [regions, setRegions]   = useState([]);
  const [psmUsers, setPsmUsers] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState(['Direct Debit', 'Wise', 'Bank Transfer', 'Credit Card', 'BACS']);
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting]     = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'profile';
  const setActiveTab = (id) => setSearchParams(id === 'profile' ? {} : { tab: id }, { replace: true });

  const load = useCallback(async () => {
    try {
      const { data } = await customersAPI.get(id);
      setCustomer(data);
    } catch {
      toast.error('Partner not found');
      navigate('/customers');
    } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    adminAPI.getRegions().then(r => setRegions(r.data || [])).catch(() => {});
    usersAPI.directory('SALES').then(r => setPsmUsers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    adminAPI.getDropdownOptions('payment_method')
      .then(r => { const opts = (r.data || []).map(o => o.label); if (opts.length) setPaymentMethods(opts); })
      .catch(() => {});
  }, []);

  const toggleStatus = async () => {
    const newStatus = customer.status === 'active' ? 'inactive' : 'active';
    try {
      await customersAPI.update(id, { status: newStatus });
      toast.success(`Partner marked as ${newStatus}`);
      load();
    } catch { toast.error('Failed to update status'); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await customersAPI.delete(id);
      toast.success('Partner deleted');
      navigate('/customers');
    } catch { toast.error('Failed to delete'); }
    finally { setDeleting(false); }
  };

  if (loading) return (
    <div className="space-y-4">
      <Skeleton className="h-20 w-full" />
      <Skeleton className="h-12 w-60" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
  if (!customer) return null;

  const isActive  = customer.status === 'active';
  const canManage = isAdmin || isManager;
  const isSuperAdmin = authUser?.role === 'SUPER_ADMIN';

  return (
    <div className="flex flex-col min-h-full" style={{ background: '#f8fafc' }} data-testid="customer-detail-page">

      {/* ── Sticky Header ── */}
      <div className="bg-white flex-shrink-0" style={{ borderBottom: '1px solid #e2e8f0' }}>
        <div className="px-8 pt-6 pb-4">
          {/* Breadcrumb */}
          <Link to="/customers">
            <button className="flex items-center gap-1.5 text-xs font-medium mb-4 hover:opacity-70 transition-opacity"
              style={{ color: 'hsl(var(--muted-fg))' }}>
              <ArrowLeft size={13} /> Partners
            </button>
          </Link>
          {/* Identity row */}
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold flex-shrink-0"
                style={{ background: 'hsl(var(--c4p-brand-blue) / 0.12)', color: NAVY }}>
                {customer.company_name?.charAt(0).toUpperCase() || 'P'}
              </div>
              <div>
                <div className="flex items-center gap-2.5 flex-wrap">
                  <h2 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-display)', color: NAVY }}>
                    {customer.company_name}
                  </h2>
                  <Badge variant="outline" className="text-xs">{customer.partner_type}</Badge>
                  <Badge variant="outline" className="text-xs font-medium"
                    style={isActive
                      ? { background: '#f0fdf4', color: '#15803d', borderColor: '#bbf7d0' }
                      : { background: '#f8fafc', color: '#475569', borderColor: '#e2e8f0' }}
                    data-testid="partner-status-badge">
                    {isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <div className="flex items-center gap-3 mt-1 flex-wrap text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
                  {customer.country && <span>{customer.country}</span>}
                  {customer.default_currency && <span className="font-mono">{customer.default_currency}</span>}
                  {customer.default_region_name && <span className="flex items-center gap-0.5"><Globe size={11} /> {customer.default_region_name}</span>}
                  {customer.default_psm_name && <span>PSM: {customer.default_psm_name}</span>}
                </div>
              </div>
            </div>
            {canManage && (
              <div className="flex items-center gap-2 flex-shrink-0">
                {isSuperAdmin && (
                  <GdprExportButton entityId={customer.id} entityType="customer" entityName={customer.company_name} />
                )}
                <Button variant="outline" size="sm" onClick={toggleStatus} className="gap-1.5"
                  style={isActive ? { color: '#d97706', borderColor: '#fde68a' } : { color: '#15803d', borderColor: '#bbf7d0' }}
                  data-testid="partner-toggle-status-button">
                  {isActive ? <PowerOff size={13} /> : <Power size={13} />}
                  {isActive ? 'Deactivate' : 'Activate'}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setShowDelete(true)}
                  style={{ color: 'hsl(var(--danger))' }} data-testid="partner-delete-button">
                  <Trash2 size={13} />
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Tab bar — bottom-border style */}
        <div className="px-8 flex gap-0.5" style={{ background: 'white' }}>
          {[
            { id: 'profile',   label: 'Profile' },
            { id: 'contacts',  label: `Contacts (${(customer.contacts || []).length})` },
            { id: 'quotes',    label: 'Quotes' },
            { id: 'contracts', label: 'Contracts' },
          ].map(({ id, label }) => (
            <button key={id} onClick={() => setActiveTab(id)}
              data-testid={`tab-${id}`}
              className="px-4 py-3 text-sm font-medium transition-colors outline-none"
              style={{
                color: activeTab === id ? NAVY : '#64748b',
                fontWeight: activeTab === id ? 600 : 400,
                borderBottom: activeTab === id ? `2px solid ${NAVY}` : '2px solid transparent',
                background: 'transparent',
              }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {!isActive && (
        <div className="px-8 pt-6">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              This partner is <strong>inactive</strong> - they will not appear in partner dropdowns when creating new quotes.
            </AlertDescription>
          </Alert>
        </div>
      )}

      {/* ── Tab content ── */}
      <div className="flex-1 px-8 py-8">
        {activeTab === 'profile'   && <ProfileTab customer={customer} regions={regions} psmUsers={psmUsers} paymentMethods={paymentMethods} onSaved={load} />}
        {activeTab === 'contacts'  && <CustomerContactsTab customer={customer} onReload={load} />}
        {activeTab === 'quotes'    && <QuotesTab customerId={customer.id} companyName={customer.company_name} />}
        {activeTab === 'contracts' && <ContractsTab customerId={customer.id} companyName={customer.company_name} />}
      </div>

      {/* Delete confirmation */}
      <Dialog open={showDelete} onOpenChange={setShowDelete}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle size={18} style={{ color: 'hsl(var(--danger))' }} /> Delete Partner
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
            Are you sure you want to delete <strong>{customer.company_name}</strong>?
            This cannot be undone. Existing quotes will not be affected.
          </p>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setShowDelete(false)}>Cancel</Button>
            <Button onClick={handleDelete} disabled={deleting}
              style={{ background: 'hsl(var(--danger))', color: 'white' }}
              data-testid="confirm-delete-partner">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete Partner'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
